import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { LazyLoadImage } from 'react-lazy-load-image-component'

import { HeartIcon, LeftArrowThinIcon, RightArrowThinIcon, GoogleLogo, SearchIcon, MoreVertIcon, ShareIcon } from 'images'
import { useTranslation } from 'react-i18next'

// Google Discover feed carousel: advertiser row (logo + name + "Sponsored"),
// a swipeable strip of plain creative images (no per-card text, unlike
// Meta's carousel), a shared long headline below the strip, and the
// Discover post's save/share/overflow action row.
//
// Reuses the same `.carousel-preview-wrapper`/`.button-scroll` chevron-nav
// chrome and scroll-tracking mechanics as Meta's carousel (CarouselMedia.jsx's
// facebook branch), for visual/interaction consistency between the two
// carousel previews in this modal.
const DemandGenCarouselCard = ({ ad }) => {
  const { businessName, logo, carouselCards, body } = ad

  const trackRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const { t } = useTranslation()

  const updateScrollState = useCallback(() => {
    if (!trackRef.current) return
    const { scrollLeft, scrollWidth, clientWidth } = trackRef.current
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth)
    setCanScrollLeft(scrollLeft > 1)
    setCanScrollRight(scrollLeft < maxScrollLeft - 1)
  }, [])

  useEffect(() => {
    const frameId = window.requestAnimationFrame(updateScrollState)
    return () => window.cancelAnimationFrame(frameId)
  }, [carouselCards, updateScrollState])

  const handleTrackScroll = (direction) => {
    if (!trackRef.current) return
    const scrollOffset = 200
    const { scrollLeft, scrollWidth, clientWidth } = trackRef.current
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth)
    const nextScrollLeft =
      direction === 'left'
        ? Math.max(0, scrollLeft - scrollOffset)
        : Math.min(maxScrollLeft, scrollLeft + scrollOffset)

    if (Math.abs(nextScrollLeft - scrollLeft) < 1) return
    trackRef.current.scrollTo({ left: nextScrollLeft, behavior: 'smooth' })
  }

  return (
    <div className="google-display-card google-display-card--demand-carousel">
      <div className='demand-gen-carousel-header'>
        <GoogleLogo width={90} />

        <div className='demand-gen-carousel-header_searchbar'>
          <p>{t('common.search')}</p>

          <SearchIcon width={16} height={16} />
        </div>
      </div>

      <div className='demand-gen-carousel-body'>
        <div className="google-display-card__discover-header">
          {logo?.url ? (
            <img className="google-display-card__logo" src={logo.url} alt={businessName} />
          ) : (
            <span className="google-display-card__logo-fallback" />
          )}

          <div>
            <span className="google-display-card__advertiser">{businessName || 'Business Name'}</span>

            <span className="google-display-card__sponsored">Sponsored</span>
          </div>
        </div>

        <div className="carousel-preview-wrapper">
          <button
            type="button"
            className="button-scroll button-scroll-left"
            onClick={() => handleTrackScroll('left')}
            disabled={!canScrollLeft}
          >
            <LeftArrowThinIcon width={8} />
          </button>

          <div
            className="google-display-card__discover-carousel"
            ref={trackRef}
            onScroll={updateScrollState}
          >
            {carouselCards.map((card, index) => (
              <LazyLoadImage
                key={card.id ?? index}
                className="google-display-card__discover-carousel-item"
                src={card.media?.url}
                alt={card.media?.filename}
                width={card.media?.width}
                height={card.media?.height}
                visibleByDefault
              />
            ))}
          </div>
          <button
            type="button"
            className="button-scroll button-scroll-right"
            onClick={() => handleTrackScroll('right')}
            disabled={!canScrollRight}
          >
            <RightArrowThinIcon width={8} height={8} />
          </button>
        </div>

        <div className="google-display-card__description">{body}</div>
      </div>

      <div className="google-display-card__discover-actions">
        <HeartIcon width={16} height={16} />
        <ShareIcon width={16} height={16} />
        <MoreVertIcon width={16} height={16} />
      </div>
    </div>
  )
}

DemandGenCarouselCard.propTypes = {
  ad: PropTypes.shape({
    businessName: PropTypes.string,
    logo: PropTypes.object,
    headline: PropTypes.string,
    longHeadline: PropTypes.string,
    body: PropTypes.string,
    carouselCards: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        media: PropTypes.object,
      }),
    ).isRequired,
  }).isRequired,
}

export default DemandGenCarouselCard
